import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { motion } from "framer-motion";
import { ProductLogo } from "./ProductLogo";

const ACCENT = "#4F52C0";

const fade = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
};

const Eyebrow = ({ children }) => (
  <p
    className="font-mono text-[10px] uppercase tracking-eyebrow"
    style={{ color: ACCENT }}
  >
    {children}
  </p>
);

const TopBar = () => (
  <header className="sticky top-0 z-40 border-b border-hairline bg-canvas/80 backdrop-blur-xl">
    <div className="mx-auto flex h-16 max-w-[1120px] items-center justify-between px-6">
      <Link
        to="/"
        data-testid="demo-back"
        className="inline-flex items-center gap-2 text-[14px] font-medium text-slatetx transition-colors hover:text-ink"
      >
        <ArrowLeft size={16} />
        MisiDigi
      </Link>
      <span className="font-mono text-[10px] uppercase tracking-eyebrow text-platinum">
        Product Demo
      </span>
    </div>
  </header>
);

const ScreenFrame = ({ src, alt, testid }) => (
  <div
    data-testid={testid}
    className="mx-auto w-full max-w-[390px] overflow-hidden rounded-[24px] border border-hairline bg-surface card-shadow"
  >
    <img
      src={src}
      alt={alt}
      loading="lazy"
      className="block h-auto w-full object-contain"
    />
  </div>
);

const METHOD = [
  {
    title: "The Idea",
    body: "Understand the reasoning through short sections, concrete examples, and clear distinctions.",
  },
  {
    title: "The Lens",
    body: "Use one thoughtful question to notice where the idea appears in your own routines, work, or decisions.",
  },
  {
    title: "The Connection",
    body: "See where ideas from different books reinforce, challenge, or clarify one another.",
  },
];

export const MindBriefDemo = () => {
  return (
    <div
      data-testid="mindbrief-demo"
      className="min-h-screen bg-canvas"
    >
      <TopBar />

      <main className="mx-auto max-w-[1120px] px-6 py-12 sm:py-16">
        {/* 1. Hero */}
        <motion.div {...fade}>
          <ProductLogo
            product={{ id: "mindbrief", name: "MindBrief", logo: "/logos/mindbrief.png" }}
            rounded="rounded-[22%]"
            className="h-16 w-16 card-shadow"
          />
          <h1 className="mt-5 text-[clamp(2rem,5vw,2.75rem)] font-extrabold tracking-tight text-ink">
            MindBrief
          </h1>
          <p className="mt-2 text-[17px] font-semibold text-ink">
            Ideas become more useful when they connect.
          </p>
          <p className="mt-3 max-w-xl text-[15px] leading-relaxed text-slatetx">
            Read a clear brief when time is short. Go deeper when an idea
            deserves more attention. Then see how it connects with other books
            and your own decisions.
          </p>
          <div
            data-testid="mindbrief-proof-row"
            className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-[13px] text-slatetx"
          >
            <span>51 books</span>
            <span className="h-3 w-px bg-hairline" aria-hidden="true" />
            <span>Quick Read + Go Deeper</span>
            <span className="h-3 w-px bg-hairline" aria-hidden="true" />
            <span>Lenses and cross-book connections</span>
          </div>
        </motion.div>

        {/* 2. Library */}
        <motion.section
          {...fade}
          className="mt-16 grid items-center gap-10 border-t border-hairline pt-14 md:grid-cols-2"
        >
          <div>
            <Eyebrow>Your reading library</Eyebrow>
            <h2 className="mt-4 text-[clamp(1.5rem,3vw,2rem)] font-bold leading-tight tracking-tight text-ink">
              Find the book. Return when you need it.
            </h2>
            <p className="mt-4 max-w-md text-[15px] leading-relaxed text-slatetx">
              Browse 51 books by shelf, search by title or idea, and return to
              something you recently read or saved. Reading time and available
              connections are visible before you open a book.
            </p>
          </div>
          <ScreenFrame
            src="/demo/mindbrief_library.png"
            alt="MindBrief library with shelves and recently viewed books"
            testid="mindbrief-library-shot"
          />
        </motion.section>

        {/* 3. Reader */}
        <motion.section
          {...fade}
          className="mt-16 grid items-center gap-10 border-t border-hairline pt-14 md:grid-cols-2"
        >
          <div className="order-2 md:order-1">
            <ScreenFrame
              src="/demo/mindbrief_reader.png"
              alt="MindBrief reader showing Atomic Habits in Go Deeper mode"
              testid="mindbrief-reader-shot"
            />
          </div>
          <div className="order-1 md:order-2">
            <Eyebrow>Choose your depth</Eyebrow>
            <h2 className="mt-4 text-[clamp(1.5rem,3vw,2rem)] font-bold leading-tight tracking-tight text-ink">
              Start quickly. Stay when the idea matters.
            </h2>
            <div className="mt-6 space-y-5">
              <div>
                <h3 className="text-[15.5px] font-bold tracking-tight text-ink">
                  Quick Read
                </h3>
                <p className="mt-1.5 text-[14.5px] leading-relaxed text-slatetx">
                  A structured reading path for the book's central argument,
                  written for limited time without reducing it to a list of
                  tips.
                </p>
              </div>
              <div className="border-t border-hairline pt-5">
                <h3 className="text-[15.5px] font-bold tracking-tight text-ink">
                  Go Deeper
                </h3>
                <p className="mt-1.5 text-[14.5px] leading-relaxed text-slatetx">
                  More context, distinctions, examples, and questions that help
                  the reader understand how the argument works and where its
                  limits are.
                </p>
              </div>
            </div>
            <p className="mt-5 text-[12.5px] italic leading-relaxed text-platinum">
              Reading time comes from the rendered content, so each mode
              reflects the material actually available.
            </p>
          </div>
        </motion.section>

        {/* 4. The MindBrief method */}
        <motion.section
          {...fade}
          className="mt-16 border-t border-hairline pt-14"
        >
          <Eyebrow>From reading to judgment</Eyebrow>
          <div className="mt-6 grid gap-4 sm:grid-cols-3">
            {METHOD.map((m) => (
              <div
                key={m.title}
                data-testid="mindbrief-method-card"
                className="rounded-[20px] border border-hairline bg-surface p-6 card-shadow"
              >
                <h3 className="font-mono text-[10.5px] uppercase tracking-[0.16em] text-ink">
                  {m.title}
                </h3>
                <p className="mt-3 text-[13.5px] leading-relaxed text-slatetx">
                  {m.body}
                </p>
              </div>
            ))}
          </div>

          {/* Lens example — soft lavender squircle */}
          <div
            data-testid="mindbrief-lens-example"
            className="mt-6 rounded-[24px] p-7 sm:p-8"
            style={{ backgroundColor: "#EEEDFB" }}
          >
            <span
              className="font-mono text-[10px] uppercase tracking-[0.16em]"
              style={{ color: ACCENT }}
            >
              The Lens
            </span>
            <p className="mt-3 max-w-xl text-[17px] font-medium leading-relaxed text-ink">
              Where am I asking for more discipline when I could remove some
              friction instead?
            </p>
          </div>

          {/* Tension example — white editorial card */}
          <div
            data-testid="mindbrief-tension-card"
            className="mt-4 rounded-[24px] border border-hairline bg-surface p-7 card-shadow sm:p-8"
          >
            <span
              className="font-mono text-[10px] uppercase tracking-[0.16em]"
              style={{ color: ACCENT }}
            >
              MindBrief Tension
            </span>
            <p className="mt-3 max-w-xl text-[17px] font-medium leading-relaxed text-ink">
              Keep the identity. Stay flexible about the method.
            </p>
            <p className="mt-3 text-[13.5px] leading-relaxed text-slatetx">
              Black Box Thinking · Matthew Syed
              <br />
              Atomic Habits · James Clear
            </p>
          </div>

          <p className="mt-4 text-[12.5px] italic leading-relaxed text-platinum">
            MindBrief connections are editorial syntheses across books, not
            quotations from the authors.
          </p>
        </motion.section>

        {/* Soft teaser — matches the shared demo ending */}
        <motion.section {...fade} className="mt-16 border-t border-hairline pt-12">
          <p className="mb-4 text-center font-mono text-[10px] uppercase tracking-eyebrow text-platinum">
            More inside MindBrief
          </p>
          <div className="grid gap-4 sm:grid-cols-2">
            {[
              { title: "Range × Mastery", tag: "Connection" },
              { title: "The Lens: Thinking in Systems", tag: "Lens" },
            ].map((c) => (
              <div
                key={c.title}
                data-testid="demo-teaser-card"
                className="relative overflow-hidden rounded-[18px] border border-hairline bg-surface p-6"
              >
                <div className="select-none blur-[3px]">
                  <span className="font-mono text-[10px] uppercase tracking-eyebrow text-platinum">
                    {c.tag}
                  </span>
                  <h3 className="mt-2 text-[16px] font-bold tracking-tight text-ink">
                    {c.title}
                  </h3>
                  <p className="mt-2 text-[13.5px] leading-relaxed text-slatetx">
                    A quiet look at how this idea meets another book — where
                    they agree, where they push apart, and what that means for
                    a real decision.
                  </p>
                  <p className="mt-2 text-[13.5px] leading-relaxed text-slatetx">
                    Then one question that brings the connection back to your
                    own work this week.
                  </p>
                </div>
                <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-surface via-surface/70 to-transparent" />
                <span
                  className="absolute right-5 top-5 rounded-full px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.14em] text-white"
                  style={{ backgroundColor: ACCENT }}
                >
                  {c.tag}
                </span>
              </div>
            ))}
          </div>
        </motion.section>

        {/* 5. Closing */}
        <motion.section
          {...fade}
          className="mt-16 border-t border-hairline pt-14 text-center"
        >
          <h2 className="text-[clamp(1.6rem,3.5vw,2.25rem)] font-bold tracking-tight text-ink">
            Read. Understand. Connect. Apply. Revisit.
          </h2>
          <p className="mx-auto mt-4 max-w-md text-[15px] leading-relaxed text-slatetx">
            MindBrief gives useful ideas somewhere to live after you close the
            book.
          </p>
          <Link
            to="/"
            data-testid="demo-back-bottom"
            className="mt-8 inline-flex items-center gap-2 text-[14px] font-semibold text-ink transition-colors hover:text-steel"
          >
            <ArrowLeft size={15} />
            Back to MisiDigi
          </Link>
        </motion.section>
      </main>
    </div>
  );
};
